import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BasicScrollView } from "@/components/BasicScrollView";
import { FlatList, Image, Keyboard, Platform, Pressable, RefreshControl, StyleSheet, useWindowDimensions, View } from 'react-native';
import Head from 'expo-router/head';
import { Button, CloseIcon, Hourglass, TextInput } from 'react95-native-rabbl';
import { Community } from "@/types/community";
import Footer from '@/components/Footer';
import { Link, useLocalSearchParams, useFocusEffect, useRouter } from 'expo-router';
import { ExternalLink } from '@/components/ExternalLink';
import { CustomText } from '@/components/CustomText';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppConsts from '@/utilities/AppConsts';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { Video, IVideo } from '@/components/Video';
import { RabblSearchResult } from "@/components/RabblSearchResult";
import { useHeaderHeight } from "expo-router/react-navigation";
import { useBottomTabBarHeight } from "expo-router/js-tabs";
import { debounce } from 'lodash';
import { AppContext } from '@/components/AppContext';
import { AppText } from '@/components/AppText';
import { appendPublicRabblsSearchResults, setData, setPublicRabblsSearchString } from '@/state/user';
import { BasicWrapperView } from '@/components/BasicWrapperView';
// @ts-ignore
import { RefreshControl as WebRefreshControl } from 'react-native-web-refresh-control';

const RefreshComponent = Platform.OS === 'web' ? WebRefreshControl : RefreshControl;

// Text under the image
const TEXT_FOOTER_HEIGHT = 50;
const ROW_SEPARATOR_HEIGHT = 30;
const ITEM_HORIZONTAL_PADDING = 30;
const ITEM_VERTICAL_PADDING = 20;
const ITEM_COMPONENTS_GAP = 10;
const ITEM_COMPONENTS = 3;

// Search box, etc.
const HEADER_HEIGHT = 100;

const NON_IMAGE_HEIGHT =
        TEXT_FOOTER_HEIGHT +
        (ITEM_COMPONENTS_GAP * ITEM_COMPONENTS) +
        (2 * ITEM_VERTICAL_PADDING)
      ;
const ITEM_WIDTH = AppConsts.RABBL_SEARCH_RESULT_WIDTH;

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 20,
  },
  container: {
    width: '95%',
  },
  textInput: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  image: {
    width: '100%',
    height: 200,
  },
  createButton: {
    paddingTop: 20,
    alignItems: 'center',
  },
  padding3: {
    padding: 3,
  },
  padding5: {
    padding: 5,
  },
  videoWrapper: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingTop: 20,
    paddingBottom: 20,
    gap: 20,
  },
  noResultsWrapper: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 20,
  },
  rowSeparator: {
    height: ROW_SEPARATOR_HEIGHT,
    backgroundColor: 'transparent',
  },
  listContentContainerStyle: {
    alignItems: 'center',
  },
  itemWrapper: {
    alignItems: 'center',
    marginHorizontal: ITEM_HORIZONTAL_PADDING,
    paddingVertical: ITEM_VERTICAL_PADDING,
    backgroundColor: 'transparent',
    gap: ITEM_COMPONENTS_GAP,
    //borderWidth: 1,
  },
});

function ResultsHeader() : React.ReactNode {
  const dispatch = useDispatch();
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const search_string = useSelector((state: RootState) => state.user.public_rabbls_search);
  const [searchQuery, onSearchChange] = useState<string|undefined>(search_string);
  const isFirstLoad = useRef(true);

  const search = useCallback(async (str: string|undefined) => {
    if (search_string !== undefined) {
      AppUtilities.info("findrabbl ResultsHeader search: " + str);
      try {
        showSpinner();
        const response = await AppUtilities.fetchUnauthorized(
          "findrabbl ResultsHeader.search",
          "/rabbl/api/findrabbls",
          "POST",
          {
            offset: 0,
            search_string: str,
          }
        );
        dispatch(setData(response));
      } catch (e) {
        AppUtilities.logException(e, "findrabbl ResultsHeader.search");
      } finally {
        hideSpinner();
      }
    }
  }, []);

  useEffect(() => {
    let shouldSearch = true;
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      if (!search_string || search_string.length == 0) {
        shouldSearch = false;
      }
    }
    if (shouldSearch) {
      AppUtilities.debug("findrabbl ResultsHeader useEffect searching for " + search_string);
      search(search_string);
    }
  }, [search_string, search]);

  const debouncedSearch = useCallback(
    debounce((str: string|undefined) => {
      dispatch(setPublicRabblsSearchString(str ?? ""));
    }, 300),
    [],
  );

  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [searchQuery]);

  const onUpdateSearch = (str: string) => {
    onSearchChange(str);
  };

  return (
    <View style={{ padding: 10, backgroundColor: "transparent" }}>
      <View style={{ flexDirection: "row", alignItems: "center", backgroundColor: "transparent" }}>
        <TextInput value={searchQuery ?? ""} placeholder="Search" onChangeText={onUpdateSearch} style={styles.textInput} />
        {
          searchQuery && searchQuery.length > 0 ?
              <Pressable style={AppConstants.STYLES.hoveringButtonRight} hitSlop={20} onPress={() => {Keyboard.dismiss(); onUpdateSearch('');}}>
                <CloseIcon />
              </Pressable>
            :
              null
        }
      </View>
    </View>
  );
}

function NoResults() : React.ReactNode {
  return (
    <>
      <View style={styles.noResultsWrapper}>
        <CustomText>Nothing found</CustomText>
      </View>
    </>
  );
}

function removeCommunities(communities: Community[], skip_unique_ids: string[]) : Community[] {
  return communities.filter((c) => skip_unique_ids.find((c2) => c.uniqueid == c2) == undefined );
}

interface Props {
  footer?: React.ComponentType<any> | React.ReactElement,
  extraUnusableHeight?: number,
  onPress?: (community: Community) => void,
  skip_unique_ids?: string[],
}

export function RabblsSearch({
  footer,
  extraUnusableHeight = 0,
  onPress = undefined,
  skip_unique_ids = undefined,
} : Props) {
  const { search } = useLocalSearchParams<{ search: string }>();
  const accumulated_public_rabbls = useSelector((state: RootState) => state.user.accumulated_public_rabbls);
  const showTest = useSelector((state: RootState) => state.user.showTest);
  const public_rabbls_next_search_offset = useSelector((state: RootState) => state.user.public_rabbls_next_search_offset);
  const dispatch = useDispatch();
  const headerHeight = useHeaderHeight();
  const { width, height } = useWindowDimensions();
  const [ shouldCheckOnEndReached, setShouldCheckOnEndReached ] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isProcessingOnEndReached, setIsProcessingOnEndReached] = useState(false);
  const search_string = useSelector((state: RootState) => state.user.public_rabbls_search);

  const header = useMemo(() => <ResultsHeader />, []);
  const noResults = useMemo(() => <NoResults />, []);
  const separator = useMemo(() => <View style={styles.rowSeparator} />, []);
  // The 50 is to leave a bit of horizontal padding around all of them
  const maxCols = Math.floor((width - 50 - (AppConstants.BASIC_PADDING_HORIZONTAL * 2)) / (ITEM_WIDTH + (ITEM_HORIZONTAL_PADDING * 2)));
  const imageHeight = Math.floor(maxCols == 1 ? Math.max(height - headerHeight - extraUnusableHeight - NON_IMAGE_HEIGHT - HEADER_HEIGHT, AppConsts.RABBL_SEARCH_RESULT_HEIGHT) : AppConsts.RABBL_SEARCH_RESULT_HEIGHT);
  const itemHeight = imageHeight + NON_IMAGE_HEIGHT;
  const imageWidth = maxCols == 1 ? width - 40 : AppConsts.RABBL_SEARCH_RESULT_WIDTH;

  useEffect(() => {
    if (search && search.length > 0) {
      dispatch(setPublicRabblsSearchString(search));
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      AppUtilities.debug("Results.useFocusEffect focused");
      setShouldCheckOnEndReached(true);
      return () => {
        AppUtilities.debug("Results.useFocusEffect blurred");
        setShouldCheckOnEndReached(false);
      };
    }, [])
  );

  const onEndReached = useCallback(async () => {
    if (shouldCheckOnEndReached && !isProcessingOnEndReached) {
      try {
        AppUtilities.debug("findrabbl index onEndReached");
        setIsProcessingOnEndReached(true);
        //showSpinner();
        AppUtilities.debug("findrabbl Results.onEndReached searching with offset " + public_rabbls_next_search_offset);
        const response = await AppUtilities.fetchUnauthorized(
          "Results.onEndReached",
          "/rabbl/api/findrabbls",
          "POST",
          {
            offset: public_rabbls_next_search_offset,
            search_string: search_string,
          }
        );
        dispatch(appendPublicRabblsSearchResults(response));
      } catch (e) {
        AppUtilities.logException(e, "findrabbl Results.onEndReached");
      } finally {
        //hideSpinner();
        setIsProcessingOnEndReached(false);
      }
    }
  }, [shouldCheckOnEndReached, isProcessingOnEndReached, public_rabbls_next_search_offset, search_string]);

  const onRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await AppUtilities.fetchUnauthorized(
        "Results.onRefresh",
        "/rabbl/api/initialdata",
        "POST",
        {
        }
      );
      dispatch(setData(response));
    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e));
    } finally {
      setRefreshing(false);
    }
  }, []);

  return (
    <FlatList
      key={"listColumns" + maxCols}
      keyExtractor={(searchResult : Community) => searchResult.uniqueid}
      data={skip_unique_ids ? removeCommunities(accumulated_public_rabbls, skip_unique_ids) : accumulated_public_rabbls}
      renderItem={({ item }: { item: Community }) => {
        return (
          <View style={styles.itemWrapper}>
            <RabblSearchResult
              community={item}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              onPress={onPress}
            />
          </View>
        );
      }}
      style={AppConstants.STYLES.flex1}
      contentContainerStyle={styles.listContentContainerStyle}
      ListHeaderComponent={header}
      ListEmptyComponent={noResults}
      ListFooterComponent={footer}
      ItemSeparatorComponent={separator}
      getItemLayout={(data, index) => (
        {
          length: itemHeight,
          offset: Math.floor(index / maxCols) * (itemHeight + ROW_SEPARATOR_HEIGHT),
          index,
        }
      )}
      numColumns={maxCols}
      onEndReached={onEndReached}
      // How far from the end (in units of visible length of the list)
      // the trailing edge of the list must be from the end of the
      // content to trigger the onEndReached callback. Thus, a value
      // of 0.5 will trigger onEndReached when the end of the content
      // is within half the visible length of the list.
      onEndReachedThreshold={2}
      refreshing={refreshing}
      refreshControl={<RefreshComponent refreshing={refreshing} onRefresh={onRefresh} />}
    />
  );
}
